import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import linkIcon from "../../../../../assets/link.svg";
import { MESSAGE_TYPE } from "../../../../../common/constants/common.constants";
import {
  ProposalAction,
  ProposalTypeForProgram,
  GoveranceType,
} from "../../../../../common/enums/proposals.enums";
import {
  copyItemToClipboard,
  getDecimalsPerMint,
  getTreasurySymbol,
} from "../../../../../utilities/helpers";
import { createNotification } from "../../../../../utilities/notification";
import ProposalStatusAndLink from "../ProposalsStatus/ProposalStatusAndLink";
import ProposalsVoting from "../ProposalsVoting/ProposalsVoting";
import magicEdenLogo from "../../../../../assets/magicEdenLogo.svg";
import solseaLogo from "../../../../../assets/solseaLogo.svg";
import unqLogo from "../../../../../assets/unqSvgLogo.svg";
import "./ProposalsItem.scss";
import { Element } from "react-scroll";
import ProposalsItemActions from "./ProposalsItemActions/ProposalsItemActions";
import ProposalsItemBasicInfo from "./ProposalsItemBasicInfo/ProposalsItemBasicInfo";
import ProposalsItemSkeleton from "./ProposalsItemSkeleton/ProposalsItemSkeleton";
import { IClubDataForProposal } from "../../../../../common/interfaces/clubs.interface";
import { CLUB_DETAILS } from "../../../../../common/constants/routes.constants";
import { proposalsStore } from "../../../../../contexts/proposalContext";
import WithdrawalOrTransferProposalInfoTable from "../WithdrawalProposalInfoTable/WithdrawalProposalInfoTable";
import { IBasicProposalData } from "../../../../../common/interfaces/proposals.interface";
import { clubsStore } from "../../../../../contexts/clubStore";
import UpdateClubConfigTable from "../UpdateConfigTable/UpdateConfigTable";

const ProposalsItem: FC<{
  clubData: IClubDataForProposal;
  splProposal: IBasicProposalData;
  updateProposalWithMetadata?: (proposal: IBasicProposalData) => void;
}> = ({ clubData, updateProposalWithMetadata, splProposal }) => {
  const { withdrawalMembersInfo } = clubsStore();

  const { treasury } = clubsStore.getState();

  const updateProposal = proposalsStore((state) => state.updateProposal);
  const [isDetailsVisible, toggleIsDetailsVisible] = useState(false);
  const detailsRef = useRef(null);

  const proposalFromZustand = proposalsStore((state) =>
    state.proposals.find(
      (p) =>
        p.proposalBasicData.proposalAccount.pubkey.toString() ===
        splProposal?.proposalAccount.pubkey.toString()
    )
  );

  useEffect(() => {
    if (splProposal) updateProposalWithMetadata!(splProposal);
  }, [splProposal]);

  useEffect(() => {
    if (proposalFromZustand && withdrawalMembersInfo) {
      const withdrawalMemberInfo = withdrawalMembersInfo.find(
        (info) =>
          info.proposal.toString() ===
          proposalFromZustand.proposalBasicData.proposalAccount.pubkey.toString()
      );
      if (!withdrawalMemberInfo) return;
      const newMetadata = {
        ...proposalFromZustand?.metadata,
        withdrawalMembersInfo: withdrawalMemberInfo,
      };
      const existingProposalWithMetadata = {
        ...proposalFromZustand!,
        metadata: newMetadata,
      };
      updateProposal(existingProposalWithMetadata);
    }
  }, [withdrawalMembersInfo]);

  const updateProposalAfterNftVoting = async () => {
    try {
      updateProposalWithMetadata!(proposalFromZustand!.proposalBasicData);
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, "Voting failed", error.message);
    }
  };

  const copyProposalUrl = useCallback(() => {
    const proposalAddress =
      proposalFromZustand!.proposalBasicData.proposalAccount.pubkey.toString();
    let newUrl: string;

    const frontBaseUrl = process.env.REACT_APP_FRONT_BASE_URL;
    newUrl =
      frontBaseUrl +
      CLUB_DETAILS +
      "/" +
      clubData.address +
      "?proposal=" +
      proposalAddress;

    void copyItemToClipboard(newUrl, "Proposal URL copied.");
  }, [proposalFromZustand]);

  const showWithdrawalTable =
    (proposalFromZustand?.metadata.proposalAction ===
      ProposalAction.Withdrawal &&
      proposalFromZustand.metadata.withdrawalData) ||
    proposalFromZustand?.metadata.proposalAction === ProposalAction.Transfer;

  const getProposalActionFromMetaData = useMemo(() => {
    return () => {
      switch (proposalFromZustand?.metadata.proposalAction) {
        case ProposalAction.BuyNft:
        case ProposalAction.SellNft:
          return "Trading";
        case ProposalAction.Discussion:
          return "Discussion";
        case ProposalAction.Withdrawal:
          return "Withdrawal";
        case ProposalAction.Transfer:
          return "Transfer";
        case ProposalAction.UpdateGovernanceConfig:
          return `Governance config change - ${getProposalGovernanceType}`;
        case ProposalAction.UpdateRoleConfig:
          return "Change role voting power";
        case ProposalAction.AddSellPermission:
          return "Add new trade approval";
      }
    };
  }, [proposalFromZustand]);

  const getProposalGovernanceType = useMemo(() => {
    switch (proposalFromZustand?.metadata.governanceType) {
      case GoveranceType.DefaultGovernance:
        return "Default governance";
      case GoveranceType.ClubConfigGovernance:
        return "Club config governance";
      case GoveranceType.TradeApprovalGovernance:
        return "Trade approval governance";
      case GoveranceType.TransferGovernance:
        return "Transfer governance";
      case GoveranceType.WithdrawalGovernance:
        return "Withdrawal goverannce";
    }
  }, [proposalFromZustand?.metadata.governanceType]);

  const getMarketplaceLogo = useMemo(() => {
    switch (
      proposalFromZustand?.proposalBasicData.proposalMetadata.proposalType
    ) {
      case ProposalTypeForProgram.BuyNowMagicEden:
      case ProposalTypeForProgram.SellMagicEden:
        return magicEdenLogo;
      case ProposalTypeForProgram.BuySolsea:
      case ProposalTypeForProgram.SellSolsea:
        return solseaLogo;
      case ProposalTypeForProgram.BuyP2P:
      case ProposalTypeForProgram.SellP2P:
        return unqLogo;
      default:
        return undefined;
    }
  }, [proposalFromZustand]);

  const getRoleConfigData = useMemo(() => {
    const data: { title: string; amount: number; subLabel: string }[] = [];
    if (
      proposalFromZustand &&
      proposalFromZustand.proposalBasicData.proposalMetadata.proposalType ===
        ProposalTypeForProgram.UpdateRoleConfig
    ) {
      proposalFromZustand!.metadata.roleConfigData.forEach((config) => {
        data.push({
          title: config.role,
          amount: config.updateWeight,
          subLabel: "votes",
        });
      });
    }
    return data;
  }, [proposalFromZustand]);

  const getGovernanceConfigData = useMemo(() => {
    const data: { title: string; amount?: number; subLabel: string }[] = [];
    if (
      proposalFromZustand &&
      proposalFromZustand.proposalBasicData.proposalMetadata.proposalType ===
        ProposalTypeForProgram.UpdateGovernanceConfig
    ) {
      proposalFromZustand?.metadata.newQuorums.forEach((quorum) => {
        data.push({
          amount: quorum,
          subLabel: "%",
          title: "Approval vote percentage",
        });
      });
      if (proposalFromZustand?.metadata.newVotingTime) {
        data.push({
          amount: proposalFromZustand.metadata.newVotingTime,
          subLabel: "days",
          title: "Max voting time",
        });
      }
      if (
        proposalFromZustand.metadata.governanceType ===
        GoveranceType.TradeApprovalGovernance
      ) {
        const sp = treasury?.sellPermissions.find(
          (sp) =>
            sp.governance?.toString() ===
            proposalFromZustand.metadata.governancesToUpdate[0].toString()
        );

        if (sp) {
          data.push({
            amount:
              sp.from / getDecimalsPerMint(treasury?.denominatedTokenInfo),
            title: "From",
            subLabel: treasury?.denominatedTokenInfo?.symbol ?? "SOL",
          });
          data.push({
            amount: sp.to / getDecimalsPerMint(treasury?.denominatedTokenInfo),
            title: "To",
            subLabel: treasury?.denominatedTokenInfo?.symbol ?? "SOL",
          });
        }
      }
    } else if (
      proposalFromZustand &&
      proposalFromZustand.proposalBasicData.proposalMetadata.proposalType ===
        ProposalTypeForProgram.AddSellPermission
    ) {
      const symbol = getTreasurySymbol(clubData.denominatedTokenInfo);
      const decimals = +getDecimalsPerMint(clubData.denominatedTokenInfo);
      data.push({
        amount: proposalFromZustand.metadata.sellPermissionData.votingQuorum,
        title: "Add new sell permission",
        subLabel: "%",
      });
      data.push({
        amount: proposalFromZustand.metadata.sellPermissionData.from / decimals,
        title: `From`,
        subLabel: symbol,
      });
      data.push({
        amount: proposalFromZustand.metadata.sellPermissionData.to / decimals,
        title: `To`,
        subLabel: symbol,
      });
    }
    return data;
  }, [proposalFromZustand]);

  return (
    <Element name={splProposal.proposalAccount.pubkey.toString()}>
      {proposalFromZustand ? (
        <div className="proposals-item">
          <div className="proposals-item__header">
            <div>
              <div className="proposals-item__buttons">
                <p className="proposals-item__action-info">
                  {getProposalActionFromMetaData()}
                </p>
                <ProposalStatusAndLink
                  proposalWithMetadata={proposalFromZustand}
                />

                <img
                  className="proposals-item__link"
                  onClick={copyProposalUrl}
                  src={linkIcon}
                  alt="linkIcon"
                />
                {getMarketplaceLogo && (
                  <img src={getMarketplaceLogo} alt="marketplaceLogo" />
                )}
              </div>
              <ProposalsItemBasicInfo
                proposalWithMetaData={proposalFromZustand}
                voteTimeToEnd={proposalFromZustand.metadata.timeToVote}
                clubData={clubData}
              />
            </div>
            <ProposalsVoting
              clubBasicInfo={clubData}
              proposalWithMetaData={proposalFromZustand}
              proposalVotes={proposalFromZustand.metadata.proposalVotes}
              userVoteResult={proposalFromZustand.metadata.userVoteResult}
              isClubMember={!!clubData.userRole}
              updateProposalAfterNftVoting={updateProposalAfterNftVoting}
              updateProposalWithMetadata={updateProposalWithMetadata!}
            />
          </div>
          {isDetailsVisible && (
            <div ref={detailsRef} className="proposals-item__details">
              <div className="proposals-item__description-and-table">
                <p className="proposals-item__description">
                  {
                    proposalFromZustand.proposalBasicData.proposalMetadata
                      .description
                  }
                </p>
                {proposalFromZustand.proposalBasicData.proposalMetadata
                  ?.discussionLink && (
                  <a
                    href={
                      proposalFromZustand.proposalBasicData.proposalMetadata
                        .discussionLink
                    }
                    target={"_blank"}
                    rel="noopener noreferrer"
                    className="proposals-item__discussion-link"
                  >
                    {
                      proposalFromZustand.proposalBasicData.proposalMetadata
                        .discussionLink
                    }
                  </a>
                )}
                {showWithdrawalTable && (
                  <WithdrawalOrTransferProposalInfoTable
                    proposalWithMetaData={proposalFromZustand}
                    treasuryTokens={clubData.treasuryTokens}
                  />
                )}
                {proposalFromZustand.proposalBasicData.proposalMetadata
                  .proposalType === ProposalTypeForProgram.UpdateRoleConfig && (
                  <UpdateClubConfigTable data={getRoleConfigData} />
                )}
                {proposalFromZustand.proposalBasicData.proposalMetadata
                  .proposalType ===
                  ProposalTypeForProgram.UpdateGovernanceConfig && (
                  <UpdateClubConfigTable data={getGovernanceConfigData} />
                )}
                {proposalFromZustand.proposalBasicData.proposalMetadata
                  .proposalType ===
                  ProposalTypeForProgram.AddSellPermission && (
                  <UpdateClubConfigTable data={getGovernanceConfigData} />
                )}
              </div>
            </div>
          )}
          {proposalFromZustand && (
            <ProposalsItemActions
              clubBasicInfo={clubData}
              proposalWithMetaData={proposalFromZustand}
              isDetailsVisible={isDetailsVisible}
              toggleIsDetailsVisible={toggleIsDetailsVisible}
              updateProposalWithMetaData={updateProposalWithMetadata!}
            />
          )}
        </div>
      ) : (
        <ProposalsItemSkeleton />
      )}
    </Element>
  );
};

export default ProposalsItem;
